import { useEffect, useRef } from 'react';

export function useReveal(threshold = 0.05): React.RefObject<HTMLDivElement | null> {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (el === null) return;

    function inView(): boolean {
      if (el === null) return false;
      const rect = el.getBoundingClientRect();
      return rect.top < window.innerHeight - 20 && rect.bottom > 0;
    }

    // Already visible on mount — fire immediately without waiting for scroll
    if (inView()) {
      el.classList.add('visible');
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting === true) {
          el.classList.add('visible');
          cleanup();
        }
      },
      { threshold, rootMargin: '0px 0px -20px 0px' },
    );

    // Chrome fige les notifications IntersectionObserver quand un
    // scrollIntoView fluide se combine au scroll-snap (snap jamais « posé »).
    // Ce fallback sur l'événement scroll garantit le reveal dans tous les cas.
    function onScroll(): void {
      if (inView()) {
        el?.classList.add('visible');
        cleanup();
      }
    }

    function cleanup(): void {
      observer.disconnect();
      window.removeEventListener('scroll', onScroll);
    }

    observer.observe(el);
    window.addEventListener('scroll', onScroll, { passive: true });
    return cleanup;
  }, [threshold]);

  return ref;
}
